import React from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import type {
  TrustStripItem,
  TrustStripSettings,
  TrustStripStyle,
} from '../../../../services/ecommerceApi';

const EMOJI_PRESETS = ['🎧', '👤', '📄', '⭐', '🛡️', '💳', '🚚', '🏆', '🤝', '📈'];

export const DEFAULT_TRUST_STRIP: TrustStripSettings = {
  items: [
    { emoji: '🎧', value: '1000+', label: 'Aktif İşletme' },
    { emoji: '📈', value: '50K+', label: 'Aylık İşlem' },
    { emoji: '⭐', value: '99.9%', label: 'Sistem Uptime' },
    { emoji: '💬', value: '7/24', label: 'Destek Hizmeti' },
  ],
  backgroundColor: 'rgba(30, 64, 175, 0.55)',
  textColor: '#ffffff',
  mutedColor: 'rgba(255,255,255,0.85)',
  style: 'columns',
  valueBold: true,
  animateOnScroll: true,
};

const TRUST_STRIP_STYLES: TrustStripStyle[] = ['icons', 'columns', 'inline', 'editorial'];

const DEFAULT_STAT_ICONS = ['support', 'person', 'receipt'] as const;

function isDarkColor(color?: string): boolean {
  const raw = (color || '').trim();
  const hex = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!hex) return false;
  let h = hex[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.35;
}

function PreviewStatIcon({ name }: { name: string }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    viewBox: '0 0 32 32',
    width: 28,
    height: 28,
  };
  if (name === 'person') {
    return (
      <svg {...common}>
        <circle cx="16" cy="10" r="6" />
        <path d="M5 28v-2a9 9 0 0 1 9-9h4a9 9 0 0 1 9 9v2H5Z" />
      </svg>
    );
  }
  if (name === 'receipt') {
    return (
      <svg {...common}>
        <path d="M8 3h16v26l-4-3-4 3-4-3-4 3V3Z" />
        <path d="M12 10h8M12 15h8M12 20h8" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M5 17v-2a11 11 0 0 1 22 0v2" />
      <path d="M5 16h4v8H7a2 2 0 0 1-2-2v-6Zm22 0h-4v8h2a2 2 0 0 0 2-2v-6Z" />
      <path d="M23 24a4 4 0 0 1-4 4h-4" />
    </svg>
  );
}

const TEMPLATE_PRESETS: Record<
  TrustStripStyle,
  Pick<TrustStripSettings, 'style' | 'backgroundColor' | 'textColor' | 'mutedColor'>
> = {
  columns: {
    style: 'columns',
    backgroundColor: 'rgba(30, 64, 175, 0.55)',
    textColor: '#ffffff',
    mutedColor: 'rgba(255,255,255,0.85)',
  },
  inline: {
    style: 'inline',
    backgroundColor: 'rgba(30, 64, 175, 0.55)',
    textColor: '#ffffff',
    mutedColor: 'rgba(255,255,255,0.85)',
  },
  editorial: {
    style: 'editorial',
    backgroundColor: '#f6f4f0',
    textColor: '#161616',
    mutedColor: '#5c5c5c',
  },
  icons: {
    style: 'icons',
    backgroundColor: '#070d18',
    textColor: '#f0c17b',
    mutedColor: '#B8C2D0',
  },
};

const normalizeStyle = (style?: string): TrustStripStyle =>
  style === 'inline' || style === 'editorial' || style === 'icons' ? style : 'columns';

const TrustStripLivePreview: React.FC<{ settings: TrustStripSettings }> = ({ settings }) => {
  const items = (settings.items || []).filter((i) => i.value || i.label || i.emoji || i.icon);
  const showIcons =
    settings.style === 'icons' ||
    (settings.style === 'editorial' && isDarkColor(settings.backgroundColor));
  if (showIcons) {
    return (
      <Box sx={{ mt: 1, px: 2.5, py: 3, bgcolor: settings.backgroundColor || '#070d18', color: '#F8FAFC' }}>
        <Box
          sx={{
            maxWidth: 920,
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 2,
          }}
        >
          {items.map((item, index) => {
            const icon =
              item.icon === 'support' || item.icon === 'person' || item.icon === 'receipt'
                ? item.icon
                : DEFAULT_STAT_ICONS[index] || 'support';
            return (
              <Box key={`preview-${index}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25 }}>
                <Box sx={{ color: '#C78731', display: 'flex' }}>
                  {item.emoji && !item.icon ? (
                    <Box component="span" sx={{ fontSize: 26, lineHeight: 1 }}>{item.emoji}</Box>
                  ) : (
                    <PreviewStatIcon name={icon} />
                  )}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 22, letterSpacing: '-0.04em', lineHeight: 1.1, color: '#f0c17b' }}>
                    {item.value}
                  </Typography>
                  <Typography sx={{ color: '#B8C2D0', fontSize: 12, fontWeight: 600, mt: 0.5 }}>
                    {item.label}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }
  const cols = Math.min(items.length || 1, 4);
  const isEditorial = settings.style === 'editorial';
  const isInline = settings.style === 'inline';
  const textColor = settings.textColor || (isEditorial ? '#161616' : '#ffffff');
  const mutedColor =
    settings.mutedColor || (isEditorial ? '#5c5c5c' : textColorToMuted(textColor));
  const previewMaxWidth =
    isInline || cols >= 4 ? '100%' : cols === 3 ? 540 : cols === 2 ? 320 : 160;

  return (
    <Box
      sx={{
        mt: 1,
        px: 3,
        py: 2.5,
        borderRadius: isEditorial ? 0 : 3,
        bgcolor: settings.backgroundColor || (isEditorial ? '#f6f4f0' : 'rgba(30,64,175,0.55)'),
        display: isInline ? 'flex' : 'grid',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: isInline || cols < 4 ? 'center' : undefined,
        justifyItems: !isInline && cols < 4 ? 'center' : undefined,
        gridTemplateColumns: isInline
          ? undefined
          : cols >= 4
            ? 'repeat(4, 1fr)'
            : `repeat(${cols}, minmax(0, 1fr))`,
        width: previewMaxWidth,
        maxWidth: '100%',
        mx: 'auto',
        gap: isInline ? 1.5 : 2,
        textAlign: isEditorial ? 'left' : 'center',
        border: isEditorial ? '1px solid #e6e2dc' : '1px solid rgba(255,255,255,0.25)',
        borderTop: isEditorial ? '1px solid #e6e2dc' : undefined,
        backdropFilter: isEditorial ? 'none' : 'blur(8px)',
      }}
    >
      {items.map((item, index) => (
        <Box
          key={`preview-${index}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          {isInline && index > 0 && (
            <Typography component="span" sx={{ color: mutedColor, mr: 0.5 }}>
              ·
            </Typography>
          )}
          {item.emoji ? (
            <Typography component="span" sx={{ fontSize: isInline ? 16 : 22, lineHeight: 1 }}>
              {item.emoji}
            </Typography>
          ) : null}
          <Box sx={{ display: isInline ? 'inline-flex' : 'flex', flexDirection: isInline ? 'row' : 'column', alignItems: isInline ? 'baseline' : 'flex-start', gap: isInline ? 0.75 : 0 }}>
            <Typography
              sx={{
                fontWeight: settings.valueBold === false ? (isEditorial ? 500 : 600) : 800,
                fontFamily: isEditorial ? 'Georgia, "Times New Roman", serif' : 'inherit',
                fontSize: isInline ? 14 : { xs: 18, md: 24 },
                color: textColor,
                lineHeight: 1.1,
              }}
            >
              {item.value}
            </Typography>
            <Typography
              sx={{
                fontSize: isInline ? 13 : 13,
                color: mutedColor,
              }}
            >
              {item.label}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const TrustStripTemplateThumb: React.FC<{ style: TrustStripStyle; selected: boolean }> = ({
  style,
}) => {
  if (style === 'icons') {
    return (
      <Box sx={{ bgcolor: '#070d18', borderRadius: 1, px: 1, py: 1, display: 'flex', justifyContent: 'space-around', color: '#C78731' }}>
        {['support', 'person'].map((name) => (
          <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PreviewStatIcon name={name} />
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#f0c17b', lineHeight: 1.1 }}>+100</Typography>
              <Typography sx={{ fontSize: 8, color: '#B8C2D0' }}>örnek</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (style === 'editorial') {
    return (
      <Box
        sx={{
          bgcolor: '#f6f4f0',
          border: '1px solid #e6e2dc',
          borderRadius: 1,
          px: 1,
          py: 1,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 0.75,
        }}
      >
        {['31 yıl', '14 ofis'].map((value) => (
          <Box key={value}>
            <Typography
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 13,
                fontWeight: 500,
                color: '#161616',
                lineHeight: 1.1,
              }}
            >
              {value}
            </Typography>
            <Typography sx={{ fontSize: 9, color: '#5c5c5c' }}>örnek</Typography>
          </Box>
        ))}
      </Box>
    );
  }

  if (style === 'inline') {
    return (
      <Box
        sx={{
          bgcolor: 'rgba(30,64,175,0.55)',
          borderRadius: 1.5,
          px: 1,
          py: 1.25,
          color: '#fff',
          fontSize: 10,
          textAlign: 'center',
        }}
      >
        1000+ · 50K+ · 7/24
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'rgba(30,64,175,0.55)',
        borderRadius: 1.5,
        px: 1,
        py: 1,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 0.75,
        textAlign: 'center',
        color: '#fff',
      }}
    >
      {['1000+', '7/24'].map((value) => (
        <Box key={value}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
          <Typography sx={{ fontSize: 9, opacity: 0.8 }}>örnek</Typography>
        </Box>
      ))}
    </Box>
  );
};

/** Metin renginden etiket (muted) rengi üretir */
export function textColorToMuted(color: string, alpha = 0.82): string {
  const raw = (color || '').trim();
  if (!raw) return `rgba(255,255,255,${alpha})`;
  const hex = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const rgb = raw.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+\s*)?\)$/i);
  if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})`;
  return raw;
}

export const normalizeTrustStripSettings = (
  raw?: Partial<TrustStripSettings> | null
): TrustStripSettings => {
  const items = Array.isArray(raw?.items)
    ? raw!.items!
        .map((item) => ({
          value: String(item?.value || ''),
          label: String(item?.label || ''),
          emoji: String(item?.emoji || '').trim().slice(0, 8),
          icon:
            item?.icon === 'support' || item?.icon === 'person' || item?.icon === 'receipt'
              ? item.icon
              : '',
        }))
        .filter((item) => item.value || item.label || item.emoji || item.icon)
    : [];

  return {
    items: items.length ? items.slice(0, 4) : [...(DEFAULT_TRUST_STRIP.items || [])],
    backgroundColor: raw?.backgroundColor || DEFAULT_TRUST_STRIP.backgroundColor,
    textColor: raw?.textColor || DEFAULT_TRUST_STRIP.textColor,
    mutedColor: raw?.mutedColor || DEFAULT_TRUST_STRIP.mutedColor,
    style: normalizeStyle(raw?.style),
    valueBold: raw?.valueBold !== false,
    animateOnScroll: raw?.animateOnScroll !== false,
  };
};

interface TrustStripSettingsPanelProps {
  enabled: boolean;
  settings: TrustStripSettings;
  onEnabledChange: (enabled: boolean) => void;
  onSettingsChange: (settings: TrustStripSettings) => void;
  onSave: () => void | Promise<void>;
  saving?: boolean;
  saved?: boolean;
  error?: string | null;
  compact?: boolean;
}

const TrustStripSettingsPanel: React.FC<TrustStripSettingsPanelProps> = ({
  enabled,
  settings,
  onEnabledChange,
  onSettingsChange,
  onSave,
  saving = false,
  saved = false,
  error = null,
  compact = false,
}) => {
  const { t } = useTranslation('salesSystem');
  const trustStrip = normalizeTrustStripSettings(settings);

  const patchItem = (index: number, partial: Partial<TrustStripItem>) => {
    const items = [...(trustStrip.items || [])];
    items[index] = { ...items[index], ...partial };
    onSettingsChange({ ...trustStrip, items });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight="bold">
          {t('ecommerceHomeSettings.trustStrip.title')}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => onEnabledChange(e.target.checked)}
              size="small"
            />
          }
          label={t('ecommerceHomeSettings.trustStrip.enabled')}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceHomeSettings.trustStrip.hint')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {enabled && (
        <Grid container spacing={2}>
          {(trustStrip.items || []).map((item, index) => (
            <Grid item xs={12} key={`trust-item-${index}`}>
              <Grid container spacing={1.5} alignItems="flex-start">
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHomeSettings.trustStrip.emoji')}
                    value={item.emoji || ''}
                    onChange={(e) => patchItem(index, { emoji: e.target.value.slice(0, 8) })}
                    placeholder="🎧"
                    helperText={t('ecommerceHomeSettings.trustStrip.emojiHint')}
                  />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 0.5 }}>
                    {EMOJI_PRESETS.map((emoji) => (
                      <Button
                        key={`${index}-${emoji}`}
                        size="small"
                        onClick={() => patchItem(index, { emoji })}
                        sx={{
                          minWidth: 0,
                          px: 0.6,
                          py: 0.15,
                          lineHeight: 1.4,
                          borderColor: item.emoji === emoji ? 'primary.main' : 'divider',
                        }}
                        variant={item.emoji === emoji ? 'contained' : 'outlined'}
                      >
                        {emoji}
                      </Button>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHomeSettings.trustStrip.value')}
                    value={item.value || ''}
                    onChange={(e) => patchItem(index, { value: e.target.value })}
                    placeholder="+100.000"
                    helperText={t('ecommerceHomeSettings.trustStrip.valueHint')}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHomeSettings.trustStrip.label')}
                    value={item.label || ''}
                    onChange={(e) => patchItem(index, { label: e.target.value })}
                    placeholder="Aktif İşletme"
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={(trustStrip.items || []).length <= 1}
                    onClick={() => {
                      onSettingsChange({
                        ...trustStrip,
                        items: (trustStrip.items || []).filter((_, i) => i !== index),
                      });
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          ))}

          {(trustStrip.items || []).length < 4 && (
            <Grid item xs={12}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  onSettingsChange({
                    ...trustStrip,
                    items: [...(trustStrip.items || []), { emoji: '', value: '', label: '' }],
                  })
                }
              >
                {t('ecommerceHomeSettings.trustStrip.addItem')}
              </Button>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={trustStrip.valueBold !== false}
                  onChange={(e) => onSettingsChange({ ...trustStrip, valueBold: e.target.checked })}
                  size="small"
                />
              }
              label={t('ecommerceHomeSettings.trustStrip.valueBold')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={trustStrip.animateOnScroll !== false}
                  onChange={(e) =>
                    onSettingsChange({ ...trustStrip, animateOnScroll: e.target.checked })
                  }
                  size="small"
                />
              }
              label={t('ecommerceHomeSettings.trustStrip.animateOnScroll')}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {t('ecommerceHomeSettings.trustStrip.animateOnScrollHint')}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              {t('ecommerceHomeSettings.trustStrip.template')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 1.5,
              }}
            >
              {TRUST_STRIP_STYLES.map((style) => {
                const visualStyle =
                  trustStrip.style === 'icons' ||
                  (trustStrip.style === 'editorial' && isDarkColor(trustStrip.backgroundColor))
                    ? 'icons'
                    : trustStrip.style || 'columns';
                const selected = visualStyle === style;
                const labelKey =
                  style === 'icons'
                    ? 'styleIcons'
                    : style === 'editorial'
                      ? 'styleEditorial'
                      : style === 'inline'
                        ? 'styleInline'
                        : 'styleColumns';
                const hintKey = `${labelKey}Hint` as const;
                return (
                  <Box
                    key={style}
                    component="button"
                    type="button"
                    onClick={() =>
                      onSettingsChange({
                        ...trustStrip,
                        ...TEMPLATE_PRESETS[style],
                      })
                    }
                    sx={{
                      textAlign: 'left',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      p: 1.25,
                      bgcolor: 'background.paper',
                      boxShadow: selected ? 1 : 0,
                    }}
                  >
                    <TrustStripTemplateThumb style={style} selected={selected} />
                    <Typography variant="subtitle2" sx={{ mt: 1, lineHeight: 1.3 }}>
                      {t(`ecommerceHomeSettings.trustStrip.${labelKey}`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(`ecommerceHomeSettings.trustStrip.${hintKey}`)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHomeSettings.trustStrip.backgroundColor')}
              value={trustStrip.backgroundColor || ''}
              onChange={(e) =>
                onSettingsChange({ ...trustStrip, backgroundColor: e.target.value })
              }
              helperText="Örn. rgba(30,64,175,0.55)"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="input"
                      type="color"
                      value={
                        String(trustStrip.backgroundColor || '').startsWith('#')
                          ? trustStrip.backgroundColor
                          : '#1e40af'
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onSettingsChange({ ...trustStrip, backgroundColor: e.target.value })
                      }
                      sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHomeSettings.trustStrip.textColor')}
              value={trustStrip.textColor || '#ffffff'}
              onChange={(e) => {
                const textColor = e.target.value;
                onSettingsChange({
                  ...trustStrip,
                  textColor,
                  // Etiket rengi metin rengiyle birlikte güncellenir
                  mutedColor: textColorToMuted(textColor),
                });
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="input"
                      type="color"
                      value={
                        String(trustStrip.textColor || '').startsWith('#')
                          ? trustStrip.textColor
                          : '#ffffff'
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const textColor = e.target.value;
                        onSettingsChange({
                          ...trustStrip,
                          textColor,
                          mutedColor: textColorToMuted(textColor),
                        });
                      }}
                      sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHomeSettings.trustStrip.labelColor')}
              value={trustStrip.mutedColor || textColorToMuted(trustStrip.textColor || '#ffffff')}
              onChange={(e) =>
                onSettingsChange({ ...trustStrip, mutedColor: e.target.value })
              }
              helperText={t('ecommerceHomeSettings.trustStrip.labelColorHint')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="input"
                      type="color"
                      value={
                        String(trustStrip.mutedColor || '').startsWith('#')
                          ? trustStrip.mutedColor
                          : String(trustStrip.textColor || '').startsWith('#')
                            ? trustStrip.textColor
                            : '#ffffff'
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onSettingsChange({ ...trustStrip, mutedColor: e.target.value })
                      }
                      sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TrustStripLivePreview settings={trustStrip} />
          </Grid>
        </Grid>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
        <Button variant="contained" onClick={() => void onSave()} disabled={saving}>
          {saving
            ? t('ecommerceHomeSettings.trustStrip.saving')
            : enabled
              ? t('ecommerceHomeSettings.trustStrip.save')
              : t('ecommerceHomeSettings.trustStrip.saveHidden')}
        </Button>
        {saved && (
          <Typography variant="body2" color="success.main">
            {t('ecommerceHomeSettings.trustStrip.saved')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TrustStripSettingsPanel;
